/**
 * Response Processors Module
 *
 * Central module for agent response processing using the Strategy Pattern.
 * Auto-initializes all processors when imported.
 *
 * @module services/responseProcessors
 *
 * @example
 * ```typescript
 * import { responseProcessorRegistry, ProcessorContext } from '@/services/responseProcessors';
 *
 * // Get processor for an agent
 * const processor = responseProcessorRegistry.getProcessor('report-builder');
 *
 * // Process a response
 * const normalized = processor.normalizeResponse(response);
 * const result = await processor.processResponse(normalized, context);
 * ```
 */

// Export types
export * from './types';

// Export registry
export { responseProcessorRegistry } from './registry';

// Export processors (for testing/extension)
export { DefaultProcessor } from './DefaultProcessor';
export { ReportBuilderProcessor } from './ReportBuilderProcessor';
export { POAgentProcessor } from './POAgentProcessor';
export { CustomerServiceProcessor } from './CustomerServiceProcessor';

// Import processors for registration
import { responseProcessorRegistry } from './registry';
import { DefaultProcessor } from './DefaultProcessor';
import { ReportBuilderProcessor } from './ReportBuilderProcessor';
import { POAgentProcessor } from './POAgentProcessor';
import { CustomerServiceProcessor } from './CustomerServiceProcessor';

/**
 * Initialize registry with all processors
 *
 * Called automatically when this module is imported.
 * Can also be called manually for hot reload scenarios.
 */
export function initializeResponseProcessors(): void {
  // Clear any existing registrations (useful for hot reload)
  responseProcessorRegistry.clear();

  // Set default processor for agents without specialized handling
  responseProcessorRegistry.setDefault(new DefaultProcessor());

  // Register specialized processors
  responseProcessorRegistry.register(new ReportBuilderProcessor());
  responseProcessorRegistry.register(new POAgentProcessor());
  responseProcessorRegistry.register(new CustomerServiceProcessor());

}

// Auto-initialize when imported
initializeResponseProcessors();
